import React, { useCallback, useEffect, useState, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Article as ArticleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Add as AddIcon,
  History as HistoryIcon,
  EditNote as EditNoteIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { articleApi, KnowledgeEntry, WeChatArticleHistory } from '../services/api';

type HistorySidebarProps = {
  historyList: WeChatArticleHistory[];
  selectedHistoryId: number | null;
  historyLoading: boolean;
  onNewConversation: () => void;
  onOpenHistory: (item: WeChatArticleHistory) => void;
};

const HistorySidebar = memo(function HistorySidebar({
  historyList,
  selectedHistoryId,
  historyLoading,
  onNewConversation,
  onOpenHistory,
}: HistorySidebarProps) {
  return (
    <Paper sx={{ p: 2, alignSelf: 'start', height: { md: 'calc(100vh - 190px)' }, overflow: 'auto' }}>
      <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon fontSize="small" />
        对话历史
      </Typography>
      <Button fullWidth variant="contained" startIcon={<AddIcon />} sx={{ mb: 1.5 }} onClick={onNewConversation}>
        新增对话
      </Button>
      <Divider sx={{ mb: 1 }} />
      <List dense disablePadding>
        <ListItemButton selected={selectedHistoryId === null} onClick={onNewConversation}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <EditNoteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="新建对话" secondary="当前草稿" primaryTypographyProps={{ noWrap: true }} />
        </ListItemButton>
        {historyList.map((item) => (
          <ListItemButton
            key={item.id}
            selected={selectedHistoryId === item.id}
            onClick={() => onOpenHistory(item)}
            disabled={historyLoading && selectedHistoryId === item.id}
          >
            <ListItemText
              primary={item.topic}
              secondary={new Date(item.createdAt).toLocaleString()}
              primaryTypographyProps={{ noWrap: true }}
            />
          </ListItemButton>
        ))}
      </List>
      {historyList.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          暂无历史记录
        </Typography>
      )}
    </Paper>
  );
});

type ArticleFormProps = {
  topic: string;
  extra: string;
  loading: boolean;
  error: string | null;
  onTopicChange: (value: string) => void;
  onExtraChange: (value: string) => void;
  onErrorClose: () => void;
  onGenerate: () => void;
};

const ArticleForm = memo(function ArticleForm({
  topic,
  extra,
  loading,
  error,
  onTopicChange,
  onExtraChange,
  onErrorClose,
  onGenerate,
}: ArticleFormProps) {
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack spacing={2}>
        <TextField
          label="主题 / 核心信息"
          placeholder="例如：介绍我司新上线的知识库能力与使用场景"
          value={topic}
          onChange={(e) => onTopicChange(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          required
        />
        <TextField
          label="额外要求（可选）"
          placeholder="字数、必含要点、不要提及的数据等"
          value={extra}
          onChange={(e) => onExtraChange(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
        {error && (
          <Alert severity="error" onClose={onErrorClose}>
            {error}
          </Alert>
        )}
        <Box>
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={onGenerate}
            disabled={loading}
          >
            {loading ? '生成中…' : '根据知识库生成草稿'}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
});

type ArticlePreviewProps = {
  article: string;
  sources: KnowledgeEntry[];
};

const ArticlePreview = memo(function ArticlePreview({ article, sources }: ArticlePreviewProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyDraft = useCallback(async () => {
    if (!article) {
      return;
    }
    try {
      await navigator.clipboard.writeText(article);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = article;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  }, [article]);

  if (!article && sources.length === 0) {
    return null;
  }

  return (
    <>
      {sources.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            参考知识片段
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {sources.map((s) => (
              <Chip
                key={s.id}
                size="small"
                label={s.document?.title ? `${s.document.title} (#${s.id})` : `条目 #${s.id}`}
              />
            ))}
          </Box>
        </Paper>
      )}
      {article && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
            <Typography variant="h6">草稿预览</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyDraft}
            >
              {copied ? '已复制' : '复制草稿'}
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box
            className="wechat-article-md"
            sx={{
              '& h1, & h2, & h3': { mt: 2, mb: 1 },
              '& p': { mb: 1.5, lineHeight: 1.75 },
              '& ul': { pl: 2, mb: 1.5 },
            }}
          >
            <ReactMarkdown>{article}</ReactMarkdown>
          </Box>
        </Paper>
      )}
    </>
  );
});

const WeChatArticle: React.FC = () => {
  const [topic, setTopic] = useState('');
  const [extra, setExtra] = useState('');
  const [article, setArticle] = useState('');
  const [sources, setSources] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyList, setHistoryList] = useState<WeChatArticleHistory[]>([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await articleApi.getHistoryList();
        if (!cancelled) {
          setHistoryList(list || []);
        }
      } catch (e) {
        console.error('加载历史记录失败', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const list = await articleApi.getHistoryList();
      setHistoryList(list || []);
    } catch (e) {
      console.error('加载历史记录失败', e);
    }
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!topic.trim()) {
      setError('请填写文章主题或要点');
      return;
    }
    setLoading(true);
    setError(null);
    setArticle('');
    setSources([]);
    try {
      const data = await articleApi.generateWeChatArticle({
        topic: topic.trim(),
        extraInstructions: extra.trim() || undefined,
        maxResults: 8,
      });
      setArticle(data.article || '');
      setSources(data.sourcesUsed || []);
      setSelectedHistoryId(data.historyId ?? null);
      await loadHistory();
    } catch (err: any) {
      const d = err?.response?.data;
      const msg =
        (typeof d === 'object' && d?.message) ||
        (typeof d === 'string' ? d : null) ||
        err?.message ||
        '生成失败，请稍后重试';
      setError(typeof msg === 'string' ? msg : '生成失败');
    } finally {
      setLoading(false);
    }
  }, [topic, extra, loadHistory]);

  const handleOpenHistory = useCallback(async (item: WeChatArticleHistory) => {
    setSelectedHistoryId(item.id);
    setTopic(item.topic || '');
    setExtra(item.extraInstructions || '');
    setHistoryLoading(true);
    try {
      const detail = await articleApi.getHistoryDetail(item.id);
      setArticle(detail.article || '');
      setSources(detail.sourcesUsed || []);
      setError(null);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '加载历史详情失败';
      setError(msg);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleNewConversation = useCallback(() => {
    setSelectedHistoryId(null);
    setTopic('');
    setExtra('');
    setArticle('');
    setSources([]);
    setError(null);
  }, []);

  const activeConversationTitle =
    selectedHistoryId != null
      ? historyList.find((item) => item.id === selectedHistoryId)?.topic || '历史会话'
      : '新建对话';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
        gap: 2,
        minHeight: { md: 'calc(100vh - 180px)' },
      }}
    >
      <HistorySidebar
        historyList={historyList}
        selectedHistoryId={selectedHistoryId}
        historyLoading={historyLoading}
        onNewConversation={handleNewConversation}
        onOpenHistory={handleOpenHistory}
      />

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArticleIcon color="primary" />
          公众号文章生成
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          根据企业知识库全库自动检索并生成草稿，语气与读者画像由系统默认设定。
        </Typography>
        <Chip label={`当前会话：${activeConversationTitle}`} sx={{ mb: 2 }} />

        <ArticleForm
          topic={topic}
          extra={extra}
          loading={loading}
          error={error}
          onTopicChange={setTopic}
          onExtraChange={setExtra}
          onErrorClose={() => setError(null)}
          onGenerate={handleGenerate}
        />

        {historyLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!historyLoading && <ArticlePreview article={article} sources={sources} />}
      </Box>
    </Box>
  );
};

export default WeChatArticle;
